import React, { Fragment } from "react";
import * as ExitImage from "../../assets/images/exit-symbol.svg";
import * as InfoImage from "../../assets/images/info-symbol.svg";
import * as ManualCaptureButton from "../../assets/images/manual-capture-button.svg";
import { DocType } from "../../constants/DocType";
import { PrettyIdNames } from "../../constants/PrettyIdNames";
import { PubSubEvents } from "../../constants/PubSubEvents";
import { LoadingGrid } from "../LoadingGrid";
import { LoadingSpinner } from "../LoadingSpinner";
import "./Camera.css";
import { CameraInfoPopup } from "./CameraInfoPopup";
import { CapturedImage, Resolution, Video } from "./Video";

export interface CameraProps {
  uploadId: string;
  debug: boolean;
  docType: DocType;
  enableLocalDecode: boolean;
  mode: CameraMode;
  onExitClick: () => void;
  onImageCaptured: (image: CapturedImage) => void;
  resolution: Resolution;
}

export interface CameraState {
  captureReady: boolean;
  showInfoPopup: boolean;
}

export enum CameraMode {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
  OFF = "OFF",
}

export class Camera extends React.Component<CameraProps, CameraState> {

  constructor(props: CameraProps) {
    super (props);

    this.state = {
      captureReady: false,
      showInfoPopup: false,
    };
  }

  public render() {
    const videoClass = this.state.captureReady && this.props.mode !== CameraMode.OFF ?
      "Camera-feed-ready" : "Camera-feed-not-ready";

    return (
      <div className={"Camera-container"}>
        <Video
          uploadId={this.props.uploadId}
          className={videoClass}
          debug={this.props.debug}
          enableLocalDecode={this.props.enableLocalDecode}
          onImageCaptured={this.onImageCaptured}
          onVideoReady={this.onCameraReady}
          resolution={this.props.resolution}
        />
        {this.getExtras()}
      </div>
    );
  }

  public componentDidUpdate(prevProps: CameraProps, prevState: CameraState) {
    // if fresh move into auto, info popup closed, or capture set to ready, take auto picture
    if (this.state.captureReady && this.props.mode === CameraMode.AUTO) {
      if ((prevProps.mode !== this.props.mode) || (prevState.showInfoPopup && !this.state.showInfoPopup) ||
        (!prevState.captureReady && this.state.captureReady)) {
        PubSubEvents.captureImage(this.props.docType === DocType.LICENSE_BACK);
      }
    }
  }

  private getExtras = () => {
    if (!this.state.captureReady) {
      return (
        <div className={"Camera-load-spinner"}>
          <LoadingGrid/>
        </div>
      );
    }

    if (this.state.showInfoPopup) {
      return (
        <CameraInfoPopup
          docType={this.props.docType}
          isManual={this.props.mode === CameraMode.MANUAL}
          onDone={this.onInfoPopupDone}
        />
      );
    }

    let unique;
    switch (this.props.mode) {
      case CameraMode.AUTO:
        unique = (
          <div className={"Camera-capture-spinner"}>
              <LoadingSpinner/>
          </div>
        );
        break;
      case CameraMode.MANUAL:
        unique = (
          <img
            className={"Camera-capture-button"}
            onClick={this.onManualButtonClick}
            src={ManualCaptureButton}
          />
        );
        break;
      default: return (null);
    }

    const prettyIdName = PrettyIdNames.getIdNames(this.props.docType).longName;

    return (
      <Fragment>
        {unique}
        <div className={"Camera-id-type"}>
          <h4>
            {prettyIdName}
          </h4>
        </div>
        <img
          className={"Camera-exit-button"}
          onClick={this.props.onExitClick}
          src={ExitImage}
        />
        <img
          className={"Camera-info-button"}
          onClick={this.onInfoClick}
          src={InfoImage}
        />
      </Fragment>
    );
  };

  private onInfoClick = () => {
    this.setState({
      showInfoPopup: !this.state.showInfoPopup,
    });
    PubSubEvents.setPauseVideoActivities(true);
  };

  private onInfoPopupDone = () => {
    this.setState({
      showInfoPopup: false,
    });
    PubSubEvents.setPauseVideoActivities(false);
  };

  private onManualButtonClick = () => {
    PubSubEvents.captureImage(false);
  };

  private onCameraReady = () => {
    this.setState({
        captureReady: true,
    });
  };

  private onImageCaptured = (image: CapturedImage) => {
    if (!image || !this.state.captureReady || this.state.showInfoPopup) {
      return;
    }
    this.props.onImageCaptured(image);
  }
}



// WEBPACK FOOTER //
// ./src/components/camera/Camera.tsx